/**
 * Dashboard Data Processing Utility
 * Centralizes all attendance data processing to eliminate redundancy
 */

/**
 * Process attendance summary data for dashboard components
 * @param {Object} data - Raw attendance summary data from API
 * @returns {Object} Processed attendance data
 */
export const processAttendanceData = (data) => {
  if (!data || Object.keys(data).length === 0) {
    return {
      totalEmployees: 0,
      presentCount: 0,
      absentCount: 0,
      lateCount: 0,
      leaveCount: 0,
      onBreakCount: 0,
      attendancePercentage: 0,
      chartData: [],
      statCards: []
    };
  }

  // Extract basic counts
  const totalEmployees = data.totalEmployees || 0;
  const presentCount = data.totalPresent?.count || 0;
  const absentCount = data.totalAbsent?.count || 0;
  const lateCount = data.lateEmployees?.count || 0;
  const leaveCount = data.totalLeave?.count || 0;
  const onBreakCount = data.totalOnBreak?.count || 0;

  // Calculate attendance percentage
  const attendancePercentage = totalEmployees > 0 
    ? Math.round((presentCount / totalEmployees) * 100) 
    : 0;

  // Prepare chart data
  const chartData = [
    {
      label: 'Present',
      value: presentCount,
      color: '#48BB78',
      percentage: 0
    },
    {
      label: 'Absent',
      value: absentCount,
      color: '#F56565',
      percentage: 0
    },
    {
      label: 'On Leave',
      value: leaveCount,
      color: '#ED8936',
      percentage: 0
    },
    {
      label: 'On Break',
      value: onBreakCount,
      color: '#9F7AEA',
      percentage: 0
    }
  ];

  // Calculate percentages for chart
  const total = chartData.reduce((sum, item) => sum + item.value, 0);
  chartData.forEach(item => {
    item.percentage = total > 0 ? Math.round((item.value / total) * 100) : 0;
  });

  // Prepare stat cards data
  const statCards = [
    {
      label: "Total Employees",
      value: totalEmployees,
      icon: "FiUsers",
      color: "blue"
    },
    {
      label: "Present Today",
      value: presentCount,
      icon: "FiUserCheck",
      color: "green"
    },
    {
      label: "Absent Today",
      value: absentCount,
      icon: "FiUserX",
      color: "red"
    },
    {
      label: "Late Comers",
      value: lateCount,
      icon: "FiClock",
      color: "orange"
    },
    {
      label: "On Leave",
      value: leaveCount,
      icon: "FiCalendar",
      color: "purple"
    }
  ];

  return {
    totalEmployees,
    presentCount,
    absentCount,
    lateCount,
    leaveCount,
    onBreakCount,
    attendancePercentage,
    chartData,
    statCards,
    // Keep original data for components that need it
    rawData: data
  };
};

/**
 * Process weekly attendance data
 * @param {Object} data - Raw weekly attendance data from API
 * @returns {Object} Processed weekly data
 */
export const processWeeklyAttendanceData = (data) => {
  if (!data || Object.keys(data).length === 0) {
    return {
      weeklyData: [],
      weeklyStats: {
        totalPresent: 0,
        totalAbsent: 0,
        totalLeave: 0,
        totalLate: 0,
        totalEmployees: 0,
        weeklyAttendanceRate: 0
      },
      bestDay: null,
      worstDay: null
    };
  }

  // Process weekly data for visualization
  const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const shortDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  
  const weeklyData = weekDays.map((day, index) => {
    const dayData = data[day] || {};
    const totalPresent = dayData.totalPresent || 0;
    const totalAbsent = dayData.totalAbsent || 0;
    const totalLeave = dayData.totalLeave || 0;
    const lateComers = dayData.lateComers || 0;
    const total = totalPresent + totalAbsent + totalLeave;
    
    return {
      day: shortDays[index],
      fullDay: day,
      present: totalPresent,
      absent: totalAbsent,
      leave: totalLeave,
      late: lateComers,
      total,
      attendanceRate: total > 0 ? Math.round((totalPresent / total) * 100) : 0
    };
  });

  // Calculate weekly trends
  const totalWeeklyPresent = weeklyData.reduce((sum, day) => sum + day.present, 0);
  const totalWeeklyAbsent = weeklyData.reduce((sum, day) => sum + day.absent, 0);
  const totalWeeklyLeave = weeklyData.reduce((sum, day) => sum + day.leave, 0);
  const totalWeeklyLate = weeklyData.reduce((sum, day) => sum + day.late, 0);
  const totalWeeklyEmployees = totalWeeklyPresent + totalWeeklyAbsent + totalWeeklyLeave;
  const weeklyAttendanceRate = totalWeeklyEmployees > 0 
    ? Math.round((totalWeeklyPresent / totalWeeklyEmployees) * 100) 
    : 0;

  // Find best and worst days
  const bestDay = weeklyData.reduce((best, current) => 
    current.attendanceRate > best.attendanceRate ? current : best
  );
  const worstDay = weeklyData.reduce((worst, current) => 
    current.attendanceRate < worst.attendanceRate ? current : worst
  );

  return {
    weeklyData,
    weeklyStats: {
      totalPresent: totalWeeklyPresent,
      totalAbsent: totalWeeklyAbsent,
      totalLeave: totalWeeklyLeave,
      totalLate: totalWeeklyLate,
      totalEmployees: totalWeeklyEmployees,
      weeklyAttendanceRate
    },
    bestDay,
    worstDay
  };
};

/**
 * Get color for attendance percentage
 * @param {number} percentage - Attendance percentage
 * @returns {string} Color scheme name
 */
export const getAttendanceColor = (percentage) => {
  if (percentage >= 90) return 'green';
  if (percentage >= 75) return 'yellow';
  return 'red';
}; 